'use client'
import Link from "next/link";
import dynamic from "next/dynamic";
import useMount from "react-use/lib/useMount";
import type { LoggingEvent } from "lib/ga/logEvent";
import { Loading } from "components/Loading";
import { useEffect, useState } from "react";
import { Location, SingleProperty, SingleScheme, Siteplan, PortfolioType, ResourceImageDisplay } from "lib/types";
import ImageDisplay from "./ImageDisplay";
import Breadcrumbs from "./Breadcrumbs";
import Title from "./Title";
import Features from "./Features";
import Description from "./Description";
import Accommodation from "./Accommodation";
import Misrepresentation from "./Misrepresentation";
import Destination from "./Destination";
import Financial from "./Financial";
import Sustainability from "./Sustainability";
import ContactForm from "./ContactForm";
import UpgradeForm from "./UpgradeForm";
import LocalAgentsForm from "./LocalAgentsForm";
import logPageView from "lib/ga/pageView"
import logResultPageView from "lib/ga/resultPageView"
import Sidebar from "./Sidebar";
import AgentContactForm from "./AgentContactForm";
import Sponsored from "./Sponsored";
import { checkLoggedIn, getTrustLevel, getPayingClient } from "context/context";
import stylesGlobal from './result.module.css';
import Demographics from './Demographics';
import Downloads from './Downloads';
import KeyInfomation from './KeyInfomation';
import MoreInfo from "./MoreInfo";
import SchemeCard from "./SchemeCard";
import AssetManagers from "./AssetManagers";
const Video = dynamic(() => import("./Video"), {
    loading: () => <Loading />,
    ssr: false,
});

const Ads = dynamic(() => import("./ads"), {
    loading: () => <Loading />,
    ssr: true
})

const SiteplanDisplay = dynamic(() => import("./Siteplan"), {
    loading: () => <Loading />,
    ssr: false,
});

type agents = {
    company_id: number
}


type PropertyProps = {
    id: number,
    sponsored: boolean,
    address: any,
    rates: [],
    size: number,
    modified: string,
    features: {
        [key: string]: Feature
    },
    visibility: boolean,
    activePortfolio: PortfolioType,
    type: string,
    thumbnail_uri: string,
    availability: [],
    use_class: string,
    for_sale: boolean,
    price: number,
    annual_footfall: string,
    images: ResourceImageDisplay[],
    docType: string,
    category_code: string,
    documents: [],
    agents: agents[],
    asset_managers: agents[],
    centre_managers: agents[],
    address_details: object,
    parent: { thumbnail_uri: string, id: number, url: string, name: string },
    location: { uri: string, name: string, parents: { name: string, uri: string }[] },
    seo: object,
    owners: agents[],
    scheme_sponsored: boolean,
    isLDC: boolean,
    local_agents: [],
    researched_website: string,
    has_bid: boolean,
    paying_client: boolean,
    aboutInfo: {
        name: string,
        descMeta: string,
        uri: string,
    },
    premium: boolean,
    website: string,
    preview: boolean,
    scheme: {
        thumbnail: {
            format: {
                w200: {
                    uri: string
                }
            }
        },
        description: string,
        owners: [],
        agents: [],
        uri_lowercase: string,
        properties: [],
        sitePlan: object
        id: number
    },
    scheme_name: string
    dxStats: number
}

type Feature = {
    value: string,
    label: string
}


const Result = (props) => {
    const loggedIn = checkLoggedIn();
    const trust = getTrustLevel();
    const payingClient = getPayingClient();
    const {
        id,
        sponsored,
        address,
        rates,
        size,
        modified,
        features = {},
        visibility,
        activePortfolio,
        type,
        thumbnail_uri,
        availability,
        use_class,
        for_sale,
        price,
        annual_footfall,
        images,
        docType,
        category_code = '',
        documents = [],
        agents = [],
        address_details,
        parent,
        location,
        owners,
        scheme_sponsored,
        isLDC,
        local_agents,
        researched_website,
        has_bid,
        paying_client = false,
        aboutInfo,
        premium,
        website,
        preview = false,
        scheme,
        scheme_name,
        asset_managers,
        centre_managers,
        dxStats
    }: PropertyProps = props;
    const name = props.name || props.line1;
    const rent = props.rent_pa;
    useMount(() => {
        let newLayer: LoggingEvent = {
            url: window.location.pathname + window.location.search,
            result_id: id,
            type: docType,
            category: category_code,
            place: location ? location.name : '',
        };

        if (agents.length) newLayer.agent = agents.map((a) => a.company_id).join();
        if (owners.length) newLayer.owner = owners.map((a) => a.company_id).join();

        if (activePortfolio) newLayer.portfolio_id = activePortfolio.id;

        logPageView(newLayer);
        logResultPageView(newLayer);


    });
    const isPaid = scheme_sponsored === true || agents.length > 0;
    const premiumContent = isPaid || trust === 60 || trust === 100;
    const showLdcBanner = isLDC && (!has_bid && !paying_client);
    let agentsContactable = !showLdcBanner && !has_bid && !sponsored && !payingClient;
    const [showAllAgentContacts, setShowAllAgentContacts] = useState(false);
    let noContacts = agents.length == 0 && props.centre_managers !== null && props.centre_managers.length == 0 && props.asset_managers.length == 0 && owners.length == 0;

    const backupImage = () => {
        if (!thumbnail_uri && !parent?.thumbnail_uri) {
            return process.env.NEXT_PUBLIC_APP_CDN_URL + 'fallback-images/' + (category_code || 'SC').toLowerCase() + "-fullback.jpeg";
        }

        return `${process.env.NEXT_PUBLIC_APP_MEDIA_URL}${thumbnail_uri || parent.thumbnail_uri}`;
    };

    let description = "";

    if (features && features.description) {
        description += features.description.value;
    }
    if (features && features.location) {
        if (description.length > 0) {
            description += `<br><br>`;
        }
        description += features.location.value;
    }

    let showPage = visibility;

    useEffect(() => {
        if (!showPage) {
            let body = document.getElementsByTagName('body');
            if (body[0]) {
                body[0].style.overflow = 'hidden';
            }
        }
    });
    return (
        <div className={`${stylesGlobal.PropertyPage}`}>
            {!showPage && (
                <div className={stylesGlobal.unavilableWrapper}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <div className={stylesGlobal.propertyUnavilableMessage}>
                        <h2 className={stylesGlobal.subTitleText}>This Property is currently unavailable</h2>
                        {location && location.uri && (
                            <p className={stylesGlobal.largeText}>
                                <Link prefetch={false} href={`/shops-to-rent/${location.uri}`}> For more properties within this area click here</Link>
                            </p>
                        )}
                    </div>
                </div>
            )}
            {preview && (
                <div className={stylesGlobal.preview}><h2>You are curently viewing a property as a preview, this data may not be live.</h2></div>
            )}
            {Boolean(parent?.id) && <Sponsored resultId={id} scheme_sponsored={scheme_sponsored} sponsored={sponsored} portfolio={activePortfolio} type={docType} category={category_code} />}

            <Breadcrumbs category={category_code} type={docType} location={location} />

            {showPage && noContacts && (
                <Ads sideBar={false} />
            )}


            <div className={stylesGlobal.pageContainer} id="page-container" style={{ paddingTop: "1rem" }}>
                <div className={`${stylesGlobal.resultContainer} ${stylesGlobal.propertyOrder}`}>

                    <ImageDisplay
                        images={images || []}
                        title={name}
                        fallback={backupImage()}
                        sponsored={sponsored}
                        type={docType}
                        isPaid={isPaid}
                        portfolioName={activePortfolio?.name}
                        priority={true}
                        id={id}
                        availability={availability}
                        category={category_code}
                        unsponsoredSign={Boolean(parent?.id) && (sponsored === false || scheme_sponsored === false)}
                    />
                    <Sidebar showPage={showPage} dxStats={dxStats} property={props} isLoggedIn={loggedIn} active={props} parent={parent} sponsored={scheme_sponsored === true} payingClient={payingClient} contactable={showLdcBanner} showAllAgentContacts={showAllAgentContacts} />

                    <Title
                        title={name}
                        address={address}
                        owners={owners}
                    />


                    <Features
                        size={size}
                        use_class={use_class}
                        for_sale={for_sale}
                        price={price}
                        rent={rent}
                        annual_footfall={annual_footfall}
                        is_scheme={false}
                        britishLandLayout={false}
                        category_code={category_code}
                        owners={owners}
                    />

                    <Description headline={features?.lead?.value}
                        introduction={features?.introduction?.value}
                        description={features?.description?.value}
                        situation={features?.situation?.value}
                        keyPoints={features?.key_points ? features.key_points.value : []}
                        locationDescription={features?.location?.value}
                        detailed_specifications={features.detailed_specifications?.value} property={true} />


                    <KeyInfomation features={features} />
                    {(isPaid && features.hero_video) && <Video video={features.hero_video} />}

                    {isPaid && <Accommodation accommodation={props.accommodation} size={size} accommodationDescription={features?.accommodation?.value} />}

                    {isPaid && <AssetManagers asset_managers={asset_managers} centre_managers={centre_managers} showAllAgentContacts={showAllAgentContacts} sponsored={sponsored === true} isLoggedIn={loggedIn} property={props} type={docType} id={id} bid={has_bid} payingClient={payingClient} />}


                    {(premiumContent || agents.length > 0) && <Downloads downloads={documents} id={id} type={docType} britishLandLayout={false} />}
                    {premiumContent && <MoreInfo website={website} />}
                    {isPaid && <Financial rates={rates} features={features} />}

                    {isPaid && <Demographics features={features} portfolio={activePortfolio} />}

                    {isPaid && <Sustainability
                        sustainability={features['sustainability']}
                        epc={features['epc_rating']}
                        epc_information={features['epc_note']}
                        breeam_rating={features['breeam_rating']}
                        place_name={address?.place?.name || 'TODO'} />}

                    {(aboutInfo !== null && isPaid) && <Destination id={id} destination={aboutInfo} />}
                    {isPaid && scheme && scheme_sponsored && <SiteplanDisplay siteplan={scheme.sitePlan} properties={scheme.properties} portfolio={activePortfolio} id={id} />}
                    {isPaid && scheme && <SchemeCard category_code={category_code} thumbnail={scheme.thumbnail} description={scheme.description} owners={scheme.owners} agents={scheme.agents} uri_lowercase={scheme.uri_lowercase} id={scheme.id} sponsored={false} name={scheme_name} portfolio={activePortfolio} />}
                    <Misrepresentation />
                </div>
            </div>
            <ContactForm agents={agents} name={name} address={address_details} id={id} docType={docType} hasBid={has_bid} premium={premium} agentsContactable={!agentsContactable}/>
            <LocalAgentsForm name={name} address={address_details} id={id} docType={docType} local_agents={local_agents || []} />
            <AgentContactForm name={name} address={address_details} id={id} docType={docType} />
            {!isPaid && <UpgradeForm name={name} address={address_details} id={id} type={docType} />}
        </div>
    );
};

// Wrap the component so that we have proper acces to the initialised state
type Props = {
    type: string;
    active: SingleProperty | SingleScheme;
    parent: any;
    siblings: any;
    children: any;
    siteplan: Siteplan;
    location: Location;
    dxStats: [];
};
const PropertyIndexWithReducer = (props: Props) => {

    return (
        <Result {...props} />
    );
};

export default PropertyIndexWithReducer;
